import { __ } from '@wordpress/i18n';
import { RawHTML, useEffect, useState, useMemo } from "@wordpress/element";
import { useBlockProps, InspectorControls, RichText } from '@wordpress/block-editor';
import { PanelBody, ToggleControl, TextControl, Modal } from '@wordpress/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import * as regularIcons from '@fortawesome/free-regular-svg-icons';
import * as brandIcons from '@fortawesome/free-brands-svg-icons';
import useDebounce from '../hooks/useDebounce.jsx';

export default function Edit({ clientId, attributes, setAttributes }) {
    const {
        id,
        icon,
        faIconSize,
        faIconColor,
        isLinkable,
        url,
        openInNewTab,
        spanText
    } = attributes;

    const [filteredIconList, setFilteredIconList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!id) {
            // Set unique ID through block index only once.
            let clientIdSliced = clientId.slice(clientId.lastIndexOf("-") + 1);
            setAttributes({ id: 'hfc-list-item-' + clientIdSliced });
        }
    }, [id, clientId, setAttributes]);

    const blockProps = useBlockProps();

    // Function to filter valid FontAwesome icons
    const filterValidIcons = (iconSet) => {
        return Object.values(iconSet).filter(
            (icon) => icon && icon.prefix && icon.iconName
        );
    };

    // Memoize the icon list so it only runs once
    const iconList = useMemo(() => {
        const iconsArray = Object.entries({
            ...solidIcons,
            ...regularIcons,
            ...brandIcons
        })
            .filter(([key, icon]) =>
                icon &&
                icon.prefix &&
                icon.iconName &&
                key.startsWith('fa')
            )
            .map(([iconName, icon]) => ({
                iconName,
                icon
            }));
        return iconsArray;
    }, []);

    useEffect(() => {
        setFilteredIconList(iconList);
    }, [iconList]);

    // Add all icons to the library (solid, regular, brands)
    useEffect(() => {
        library.add(
            ...filterValidIcons(solidIcons),
            ...filterValidIcons(regularIcons),
            ...filterValidIcons(brandIcons)
        );
    }, []);

    const getFontAwesomeIcon = (iconName) => {
        const icon = solidIcons[iconName] || regularIcons[iconName] || brandIcons[iconName];
        return icon;
    };

    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        if (debouncedSearchTerm) {
            const filteredIcons = iconList.filter(icon =>
                icon.iconName.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            );
            setFilteredIconList(filteredIcons);
        } else {
            setFilteredIconList(iconList);
        }
    }, [debouncedSearchTerm, iconList]);

    const handleSearchInput = (e) => {
        setSearchTerm(e.target.value);
    };

    const openIconPicker = () => {
        setSearchTerm('');
        setFilteredIconList(iconList);
        setIsModalOpen(true);
    };

    const handleIconChange = (iconItem) => {
        if (iconItem && iconItem.iconName) {
            setAttributes({ icon: iconItem.iconName });
        } else {
            console.error('Invalid icon item:', iconItem);
        }
        setSearchTerm('');
        setFilteredIconList(iconList);
        setIsModalOpen(false);
    };

    const closeModal = () => setIsModalOpen(false);

    const fpIpDynamicStyles = `.fp-ip-modal-inner-wrapper {
        display: flex;
        flex-direction: column;
    }
    .fp-ip-modal-icons-wrapper {
        padding: 20px;
        display: grid;
        grid-template-columns: repeat(10, 1fr);
        gap: 10px;
    }
    .fp-ip-modal-icons {
        background-color: #F0F8FF;
        padding: 13px;
        position: relative;
    }
    .fp-ip-modal-icon {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #000000 !important;
        cursor: pointer;
    }   
    .fp-ip-modal-icon > svg {
        width: 30px;
        height: 30px;
    }
    .fp-tabs-icon-search {
        width: 100%;
        height: 50px;
    }
    #${id} .fp-ip-icon {
        display: flex;
        align-items: center;
    }
    #${id} .fp-ip-icon a {
        cursor: pointer;
        display: inline-block;
        line-height: 1;
    }
    #${id} .fp-ip-container .fp-ip-inner {
        cursor: pointer;
    }
    .components-panel__body .components-input-control__container {
        max-width: 100%;
    }
    .components-panel__body .components-toggle-control .components-flex {
        flex-direction: row-reverse;
    }`;

    return (
        <>
            <InspectorControls key={`inspector-controls-${id}`}>
                <PanelBody
                    title={__('Icon Pack Block Settings')}
                    icon={'admin-generic'}
                    initialOpen={true}
                    className={'frhd__control frhd__control-query'}
                >
                    <ToggleControl
                        label={__('Link Icon')}
                        checked={isLinkable}
                        onChange={(newValue) => setAttributes({ isLinkable: newValue })}
                        __nextHasNoMarginBottom
                    />
                    {isLinkable && (
                        <>
                            <TextControl
                                label={__('Link URL')}
                                value={url}
                                onChange={(newUrl) => setAttributes({ url: newUrl })}
                                placeholder={'#'}
                                __nextHasNoMarginBottom
                            />
                            <ToggleControl
                                label={__('Open in New Tab')}
                                checked={openInNewTab}
                                onChange={(newValue) => setAttributes({ openInNewTab: newValue })}
                                __nextHasNoMarginBottom
                            />
                        </>
                    )}
                </PanelBody>
            </InspectorControls>
            <div {...blockProps}>
                <RawHTML>{`<style>${fpIpDynamicStyles}</style>`}</RawHTML>
                {isLinkable ? (
                    <a
                        href={url}
                        target={openInNewTab ? '_blank' : '_self'}
                        rel="noopener"
                        className='fp-ip-inner'
                    >
                        <div className='fp-ip-icon'>
                            <FontAwesomeIcon
                                icon={getFontAwesomeIcon(icon)}
                                fontSize={faIconSize}
                                color={faIconColor}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openIconPicker();
                                }}
                            />
                        </div>
                        <RichText
                            tagName="span"
                            value={spanText}
                            onChange={(newText) => setAttributes({ spanText: newText })}
                        />
                    </a>
                ) : (
                    <div className='fp-ip-inner'>
                        <div className='fp-ip-icon'>
                            <FontAwesomeIcon
                                icon={getFontAwesomeIcon(icon)}
                                fontSize={faIconSize}
                                color={faIconColor}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openIconPicker();
                                }}
                            />
                        </div>
                        <RichText
                            tagName="span"
                            value={spanText}
                            onChange={(newText) => setAttributes({ spanText: newText })}
                        />
                    </div>
                )}
                {isModalOpen && (
                    <Modal title="Replace Icon" onRequestClose={closeModal}>
                        <div className='fp-ip-modal-inner-wrapper'>
                            <div className='fp-ip-modal-form-wrapper'>
                                <form className='fp-icons-search-form'>
                                    <input
                                        className="fp-tabs-icon-search"
                                        type="text"
                                        placeholder="Search icon"
                                        value={searchTerm}
                                        onChange={handleSearchInput}
                                    />
                                </form>
                            </div>
                            <div className='fp-ip-modal-icons-wrapper'>
                                {filteredIconList.map((iconItem, index) => (
                                    <div className='fp-ip-modal-icons' key={index}>
                                        <a
                                            href='#'
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleIconChange(iconItem);
                                            }}
                                            className='fp-ip-modal-icon'
                                        >
                                            <FontAwesomeIcon icon={iconItem.icon} />
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Modal>
                )}
            </div>
        </>
    );
}